/*
 * -----------------------------------------------------------------------
 *  <copyright file="risk-assessment-dialog.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FormControl from '@mui/material/FormControl';
import { Checkbox } from '@progress/kendo-react-inputs';
import { uniq } from 'lodash';

import OutlinedWrapper from '@components/core/outlined-wrapper';
import { useBoolean, useTranslation } from 'hooks';
import { GET_NAMED_ITEMS_BY_TYPE } from 'queries/common';
import { StudyFields, NamedItemFields } from 'queries/generated/fragments.generated';
import { GET_STUDY, SAVE_RISK_ASSESSMENT_CONFIGURATION } from 'queries/studies';
import { NamedItemType } from 'types/graphql-global-types';
import Constants from 'utils/constants';
import { generateReviewFrequencyUnitOptions } from 'utils/generator';
import NotificationService from 'utils/notification';

import 'views/components/admin/studies/study-settings-dialogs/study-settings-dialogs.scss';
import { SaveButton } from 'views/components/core/buttons';
import { CustomDialog, DialogActions, DialogContent, DialogTitle } from 'views/components/core/dialog';
import Loading from 'views/components/core/loading';
import SelectWithCategories from 'views/components/core/select-with-categories';
import {
    saveRiskAssessmentConfiguration,
    saveRiskAssessmentConfigurationVariables,
} from '@queries/generated/studies.generated';
import { GET_LIBRARY_RISKS } from '@queries/legacy-risk-assessment';

interface IFormData {
    riskLibraryCategory: string;
    evaluationModel: string;
    riskReviewWindowDays: number;
    numberTotal: boolean | null | undefined;
    numberActive: boolean | null | undefined;
    numberActiveCritical: boolean | null | undefined;
    numberActiveCtQsWithoutRisks: boolean | null | undefined;
    numberActiveCtQsWithoutFunctionalDataReviews: boolean | null | undefined;
}

const RiskAssessmentDialog = ({
    study,
    open,
    onClose,
    organisationId,
}: {
    study: StudyFields;
    open: boolean;
    onClose: Function;
    organisationId: string;
}) => {
    const riskReviewWindowDaysMin = 0;
    const riskReviewWindowDaysMax = 31;
    const { t } = useTranslation();
    const { t: tRAMWidgets } = useTranslation('ram', { keyPrefix: 'ctqWidgets' });
    const disableButton = useBoolean(true);
    const [errors, setErrors] = useState({ riskReviewWindowDays: '', evaluationModel: '' });
    const [libraryCategoryIds, setLibraryCategoryIds] = useState([]);
    const [libraryCategories, setLibraryCategories] = useState([]);
    const [saveRiskAssessmentConfigurationMutation] = useMutation<
        saveRiskAssessmentConfiguration,
        saveRiskAssessmentConfigurationVariables
    >(SAVE_RISK_ASSESSMENT_CONFIGURATION);
    const initialState = {
        riskLibraryCategory: Constants.ObjectId.Empty,
        evaluationModel: Constants.ObjectId.Empty,
        riskReviewWindowDays: 0,
        numberTotal: false,
        numberActive: false,
        numberActiveCritical: false,
        numberActiveCtQsWithoutRisks: false,
        numberActiveCtQsWithoutFunctionalDataReviews: false,
    };
    const [state, setState] = useState<IFormData>({ ...initialState });
    const initialValuesRef = useRef<any>(null);

    const [loadCategories, riskLibraryCategoriesRequest] = useLazyQuery(GET_NAMED_ITEMS_BY_TYPE);

    const [loadRisks, riskLibraryRequest] = useLazyQuery(GET_LIBRARY_RISKS);

    useEffect(() => {
        if (open) {
            loadCategories({
                variables: {
                    type: NamedItemType.RISK_LIBRARY_CATEGORY,
                },
            });
            loadRisks({
                variables: {
                    organisationId,
                    includeSystemRisks: Boolean(!organisationId),
                },
            });
        }
    }, [open]);

    useEffect(() => {
        if (open) {
            setState({
                riskLibraryCategory: study.riskLibraryCategories[0]?.id || Constants.ObjectId.Empty,
                evaluationModel: study.evaluationModelId || Constants.ObjectId.Empty,
                riskReviewWindowDays: study.riskReviewWindowDays ?? 0,
                numberTotal: study.ctqScreenWidgetsConfiguration?.numberTotal,
                numberActive: study.ctqScreenWidgetsConfiguration?.numberActive,
                numberActiveCritical: study.ctqScreenWidgetsConfiguration?.numberActiveCritical,
                numberActiveCtQsWithoutRisks: study.ctqScreenWidgetsConfiguration?.numberActiveCtQsWithoutRisks,
                numberActiveCtQsWithoutFunctionalDataReviews:
                    study.ctqScreenWidgetsConfiguration?.numberActiveCtQsWithoutFunctionalDataReviews,
            });
            // Clear any stale validation errors from a previous session so a re-opened
            // dialog always starts clean (this component stays mounted across close/reopen).
            setErrors({ riskReviewWindowDays: '', evaluationModel: '' });
        }
        initialValuesRef.current = {
            riskLibraryCategory: study.riskLibraryCategories[0]?.id || Constants.ObjectId.Empty,
            evaluationModel: study.evaluationModelId || Constants.ObjectId.Empty,
            riskReviewWindowDays: study.riskReviewWindowDays ?? 0,
            numberTotal: study.ctqScreenWidgetsConfiguration?.numberTotal,
            numberActive: study.ctqScreenWidgetsConfiguration?.numberActive,
            numberActiveCritical: study.ctqScreenWidgetsConfiguration?.numberActiveCritical,
            numberActiveCtQsWithoutRisks: study.ctqScreenWidgetsConfiguration?.numberActiveCtQsWithoutRisks,
            numberActiveCtQsWithoutFunctionalDataReviews:
                study.ctqScreenWidgetsConfiguration?.numberActiveCtQsWithoutFunctionalDataReviews,
        };
    }, [study, open]);

    useEffect(() => {
        if (riskLibraryRequest.data?.libraryRisks?.length) {
            const array =
                riskLibraryRequest.data?.libraryRisks
                    .map((libraryRisk: any) => libraryRisk.riskLibraryCategoryIds)
                    .reduce((a: any, b: any) => a.concat(b)) || [];
            setLibraryCategoryIds(uniq(array));
        } else setLibraryCategoryIds([]);
    }, [riskLibraryRequest]);

    useEffect(() => {
        if (riskLibraryCategoriesRequest.data?.namedItemsByType) {
            let array = [...riskLibraryCategoriesRequest.data.namedItemsByType];
            array =
                array
                    .filter(
                        (category: NamedItemFields) =>
                            (libraryCategoryIds.indexOf(category.id) >= 0 && category.active) ||
                            study.riskLibraryCategoryIds.indexOf(category.id) >= 0,
                    )
                    .map((category: NamedItemFields) => ({
                        id: category.id,
                        text: category.name,
                        default: false,
                        active: category.active,
                    })) || [];

            array.unshift({
                id: Constants.ObjectId.Empty,
                text: t('admin:selectRiskLibraryCategoryLabel'),
                default: true,
            });
            setLibraryCategories(array);
        }
    }, [riskLibraryCategoriesRequest, libraryCategoryIds]);

    const riskModels: {
        id: string;
        text: string;
        default?: boolean;
    }[] = study.organisation.evaluationModels
        .filter((model: any) => model.active)
        .map((model: any) => ({
            id: model.id,
            text: model.name,
        }));

    riskModels.unshift({
        id: Constants.ObjectId.Empty,
        text: t('admin:selectEvaluationModel'),
        default: true,
    });

    const numberValidation = (value: number, fieldName: string, min: number, max?: number, errorText?: string) => {
        let error = '';
        if (value > max || value < min) error = errorText;
        return {
            [fieldName]: error,
        };
    };

    const onChange = (name: string, errorText?: string, min?: number, max?: number) => (event: any) => {
        disableButton.setFalse();
        setState({
            ...state,
            [name]: event.target.value,
        });
        if (!min)
            setErrors({
                ...errors,
                [name]: '',
            });
        if (min !== undefined || max !== undefined) {
            const error = numberValidation(event.target.value, name, min, max, errorText);
            setErrors({
                ...errors,
                ...error,
            });
        }
    };

    const onCheckboxChange = (widgetField: string, showWidgetOnCtQF: boolean) => {
        setState({
            ...state,
            [widgetField]: showWidgetOnCtQF,
        });
        disableButton.setFalse();
    };

    const close = () => {
        disableButton.setTrue();
        onClose();
    };

    const isDirty = useMemo(() => {
        if (!initialValuesRef.current || !open) return false;
        return (
            state.riskLibraryCategory !== initialValuesRef.current.riskLibraryCategory ||
            state.evaluationModel !== initialValuesRef.current.evaluationModel ||
            state.riskReviewWindowDays !== initialValuesRef.current.riskReviewWindowDays ||
            state.numberTotal !== initialValuesRef.current.numberTotal ||
            state.numberActive !== initialValuesRef.current.numberActive ||
            state.numberActiveCritical !== initialValuesRef.current.numberActiveCritical ||
            state.numberActiveCtQsWithoutRisks !== initialValuesRef.current.numberActiveCtQsWithoutRisks ||
            state.numberActiveCtQsWithoutFunctionalDataReviews !==
                initialValuesRef.current.numberActiveCtQsWithoutFunctionalDataReviews
        );
    }, [state]);

    const checkForErrors = () => Object.keys(errors).filter((item) => (errors as any)[item]).length > 0;

    const errorRequest = () => {
        NotificationService.showNotification(t('errorMessage'));
        disableButton.setFalse();
    };

    const successRequest = () => {
        NotificationService.showNotification(t('admin:studySuccessfulUpdated', { id: study.recordId }));
        close();
    };

    const onUpdate = () => {
        const errorRiskEval =
            state.evaluationModel === Constants.ObjectId.Empty
                ? { evaluationModel: t('ra:requiredFieldError') }
                : { evaluationModel: '' };
        if (errorRiskEval.evaluationModel)
            setErrors({
                ...errors,
                ...errorRiskEval,
            });
        if (!checkForErrors() && !errorRiskEval.evaluationModel) {
            disableButton.setTrue();

            saveRiskAssessmentConfigurationMutation({
                variables: {
                    studyId: study.id ?? null,
                    evaluationModelId: state.evaluationModel || null,
                    riskReviewWindowDays: +state.riskReviewWindowDays || null,
                    riskLibraryCategoryIds:
                        state.riskLibraryCategory && state.riskLibraryCategory !== Constants.ObjectId.Empty
                            ? [state.riskLibraryCategory]
                            : null,
                    numberTotal: state.numberTotal,
                    numberActive: state.numberActive,
                    numberActiveCritical: state.numberActiveCritical,
                    numberActiveCtQsWithoutRisks: state.numberActiveCtQsWithoutRisks,
                    numberActiveCtQsWithoutFunctionalDataReviews: state.numberActiveCtQsWithoutFunctionalDataReviews,
                },
                refetchQueries: [{ query: GET_STUDY, variables: { id: study.id } }],
            })
                .then(() => {
                    successRequest();
                })
                .catch(() => {
                    errorRequest();
                });
        }
    };

    const riskReviewWindowDaysOptions = useMemo(
        () =>
            generateReviewFrequencyUnitOptions(
                riskReviewWindowDaysMin,
                riskReviewWindowDaysMax,
                t('ra:selectRiskReviewWindowDays'),
                true,
            ),
        [],
    );

    if (riskLibraryCategoriesRequest.loading || riskLibraryRequest.loading) return <Loading float />;

    return (
        <CustomDialog
            className="risk-assessment-dialog custom-width-popup"
            onClose={() => close()}
            fullWidth
            scroll="paper"
            maxWidth="md"
            open={open}
        >
            <DialogTitle id="risk-assessment-title">
                {t('study:editRAM')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => close()}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent className="risk-assessment-dialog-content">
                <FormControl variant="outlined" className="form-control-input" style={{ marginBottom: 35 }}>
                    <SelectWithCategories
                        showWarning={false}
                        customClassName="risk-library-category"
                        fieldSelect="riskLibraryCategory"
                        onChange={onChange}
                        label={t('admin:riskLibraryCategoryLabel')}
                        filterable
                        showLabels={false}
                        labelField="riskLibraryCategory"
                        dataArray={libraryCategories}
                        customId="select-risk-library-category"
                        currentState={state.riskLibraryCategory}
                    />
                </FormControl>
                <FormControl variant="outlined" className="form-control-input" style={{ marginBottom: 35 }}>
                    <SelectWithCategories
                        showWarning={false}
                        customClassName="evaluation-model"
                        fieldSelect="evaluationModel"
                        onChange={onChange}
                        label={`${t('admin:evaluationModel')}*`}
                        filterable={false}
                        labelField="evaluationModel"
                        dataArray={riskModels}
                        customId="select-evaluation-model"
                        currentState={state.evaluationModel}
                        error={errors.evaluationModel}
                    />
                </FormControl>
                <FormControl variant="outlined" className="form-control-input" style={{ marginBottom: 35 }}>
                    <SelectWithCategories
                        customClassName="arisk-review-window-days"
                        showWarning={false}
                        fieldSelect="riskReviewWindowDays"
                        onChange={onChange}
                        label={t('ra:riskReviewWindowDays')}
                        filterable={false}
                        labelField="riskReviewWindowDays"
                        dataArray={riskReviewWindowDaysOptions}
                        customId="arisk-review-window-days"
                        currentState={state.riskReviewWindowDays}
                        error={errors.riskReviewWindowDays}
                    />
                </FormControl>
                <OutlinedWrapper label={tRAMWidgets('ctqfScreenWidgets')}>
                    <FormControl variant="outlined" className="not-full mr k-justify-content-center">
                        <div className="screen-widgets__checkbox-grid-wrapper">
                            <Checkbox
                                rounded={null}
                                checked={state.numberTotal}
                                label={`${tRAMWidgets('numberTotal')}`}
                                data-testid="widgets-total-checkbox"
                                size="medium"
                                onChange={() => {
                                    onCheckboxChange('numberTotal', !state.numberTotal);
                                }}
                            />
                            <Checkbox
                                rounded={null}
                                checked={state.numberActive}
                                label={`${tRAMWidgets('numberActive')}`}
                                data-testid="widgets-active-checkbox"
                                size="medium"
                                onChange={() => {
                                    onCheckboxChange('numberActive', !state.numberActive);
                                }}
                            />
                            <Checkbox
                                rounded={null}
                                checked={state.numberActiveCritical}
                                label={`${tRAMWidgets('numberActiveCritical')}`}
                                data-testid="widgets-critical-checkbox"
                                size="medium"
                                onChange={() => {
                                    onCheckboxChange('numberActiveCritical', !state.numberActiveCritical);
                                }}
                            />
                            <Checkbox
                                rounded={null}
                                checked={state.numberActiveCtQsWithoutRisks}
                                label={`${tRAMWidgets('numberActiveCtQsWithoutRisks')}`}
                                data-testid="widgets-risks-checkbox"
                                size="medium"
                                onChange={() => {
                                    onCheckboxChange(
                                        'numberActiveCtQsWithoutRisks',
                                        !state.numberActiveCtQsWithoutRisks,
                                    );
                                }}
                            />
                            <div className="number-ctqs-without-functional-data">
                                <Checkbox
                                    rounded={null}
                                    checked={state.numberActiveCtQsWithoutFunctionalDataReviews}
                                    label={`${tRAMWidgets('numberActiveCtQsWithoutFunctionalDataReviews')}`}
                                    data-testid="widgets-data-reviews-checkbox"
                                    size="medium"
                                    onChange={() => {
                                        onCheckboxChange(
                                            'numberActiveCtQsWithoutFunctionalDataReviews',
                                            !state.numberActiveCtQsWithoutFunctionalDataReviews,
                                        );
                                    }}
                                />
                            </div>
                        </div>
                    </FormControl>
                </OutlinedWrapper>
            </DialogContent>
            <DialogActions className="risk-assessment-dialog-actions dialog-actions flex-end">
                <SaveButton
                    id="save-risk-assessments"
                    disabled={disableButton.value || checkForErrors() || !isDirty}
                    text="common:update"
                    onClick={onUpdate}
                />
            </DialogActions>
        </CustomDialog>
    );
};

export default RiskAssessmentDialog;
